import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { convertPdfToImage, dataURLtoFile } from '@/lib/pdfToImage';

export interface ExtractedDocumentData {
  document_number?: string;
  holder_name?: string;
  document_type?: string;
  coverage_type?: string;
  coverage_amount?: string;
  issue_date?: string;
  expiry_date?: string;
  issuing_authority?: string;
  additional_info?: Record<string, any>;
  confidence: number;
}

export function useDocumentExtraction() {
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extractDocument = async (
    file: File,
    documentType: string
  ): Promise<ExtractedDocumentData | null> => {
    // Validate file type before extraction
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!supportedTypes.includes(file.type)) {
      toast.error('Unsupported file type for AI extraction', {
        description: 'Please upload a JPG, PNG, or PDF file.'
      });
      return null;
    }

    setIsExtracting(true);
    setError(null);

    try {
      let fileToExtract = file;
      
      // Convert PDF to image if needed
      if (file.type === 'application/pdf') {
        toast.info('Converting PDF to image for analysis...', {
          description: 'Analyzing the first page'
        });
        
        try {
          const imageDataUrl = await convertPdfToImage(file);
          fileToExtract = dataURLtoFile(imageDataUrl, `${file.name}.png`);
        } catch (conversionError) {
          toast.error('PDF conversion failed', {
            description: 'Please try uploading an image instead'
          });
          return null;
        }
      }

      // Convert file to base64
      const base64Data = await fileToBase64(fileToExtract);

      // Call edge function
      const { data, error: functionError } = await supabase.functions.invoke(
        'extract-document-fields',
        {
          body: {
            imageData: base64Data,
            documentType
          }
        }
      );

      if (functionError) {
        throw functionError;
      }

      if (!data.success || !data.data) {
        throw new Error('Failed to extract document data');
      }

      const extractedData = data.data as ExtractedDocumentData;

      // Show toast based on confidence
      if (extractedData.confidence >= 0.8) {
        toast.success('Document analyzed successfully', {
          description: 'High confidence extraction. Please review the data.'
        });
      } else if (extractedData.confidence >= 0.5) {
        toast.warning('Document analyzed with medium confidence', {
          description: 'Please carefully review and verify all extracted fields.'
        });
      } else {
        toast.warning('Low confidence extraction', {
          description: 'Please verify all fields or consider manual entry.'
        });
      }

      return extractedData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to extract document fields';
      setError(errorMessage);
      console.error('Document extraction error:', err);
      
      toast.error('Document extraction failed', {
        description: 'Unable to analyze document. You can still enter information manually.'
      });
      
      return null;
    } finally {
      setIsExtracting(false);
    }
  };

  const clearError = () => setError(null);

  return {
    extractDocument,
    isExtracting,
    error,
    clearError
  };
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
